/**
 * Platform Bridge — единый API для Telegram и Max мини-приложений.
 * Определяет платформу и предоставляет универсальные методы.
 */

let _platform = null;

/**
 * Сбросить кеш платформы (для повторной проверки после загрузки async SDK)
 */
export function resetPlatformCache() {
  _platform = null;
}

/**
 * Определяет текущую платформу: 'telegram' | 'max' | 'web'
 */
export function getPlatform() {
  if (_platform) return _platform;

  // 1. URL-параметр platform — мгновенно, не зависит от загрузки SDK
  const urlParams = new URLSearchParams(window.location.search);
  const urlPlatform = urlParams.get('platform');
  if (urlPlatform === 'telegram' || urlPlatform === 'max') {
    _platform = urlPlatform;
    return _platform;
  }

  // 2. SDK initData — заполняется только внутри реальной платформы
  if (window.Telegram?.WebApp?.initData) {
    _platform = 'telegram';
  } else if (window.WebApp?.initData && !window.Telegram?.WebApp?.initData) {
    _platform = 'max';
  } else if (window.Telegram?.WebApp?.initDataUnsafe?.user) {
    _platform = 'telegram';
  } else {
    // 3. Fallback — старые URL-параметры
    if (urlParams.get('tg_id')) {
      _platform = 'telegram';
    } else if (urlParams.get('max_id')) {
      _platform = 'max';
    } else {
      // 'web' НЕ кэшируем: если вызов случился раньше, чем SDK Telegram
      // отдал initData (URL без ?platform=..., например direct-link
      // t.me/bot/app), вердикт «web» навсегда отключал telegram-фичи
      // (нативные инвойсы уходили в redirect). Пересчёт дешёвый.
      return 'web';
    }
  }

  return _platform;
}

/**
 * Пишет текущую платформу в data-атрибут документа и возвращает её.
 *
 * Нужен CSS: у Букетберри шапка в Telegram компактнее (лого по центру), а на
 * сайте и в MAX остаётся прежней. Атрибут обновляется повторно из App.jsx —
 * вердикт «web» не кэшируется, и при открытии по direct-link платформа
 * становится известна только после загрузки SDK.
 */
export function syncPlatformAttr() {
  const platform = getPlatform();
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.platform = platform;
  }
  return platform;
}

/**
 * Возвращает тип устройства: 'ios' | 'android' | 'desktop' | 'web'
 * Telegram: ios, android, tdesktop, web, weba
 * Max: ios, android, desktop, web
 */
export function getDevicePlatform() {
  const webapp = getWebApp();
  const raw = webapp?.platform || 'web';
  // Telegram 'tdesktop' → 'desktop', 'weba' → 'web'
  if (raw === 'tdesktop') return 'desktop';
  if (raw === 'weba') return 'web';
  return raw;
}

/**
 * Возвращает объект WebApp текущей платформы
 */
export function getWebApp() {
  const platform = getPlatform();
  if (platform === 'telegram') return window.Telegram?.WebApp || null;
  if (platform === 'max') return window.WebApp || null;
  return null;
}

/**
 * Возвращает initData для валидации на бэкенде
 */
export function getInitData() {
  const webapp = getWebApp();
  return webapp?.initData || '';
}

/**
 * Возвращает заголовок для API-запросов с initData
 */
export function getAuthHeader() {
  const platform = getPlatform();
  const initData = getInitData();

  if (initData) {
    if (platform === 'telegram') {
      return { 'X-Telegram-Init-Data': initData };
    }
    if (platform === 'max') {
      return { 'X-Max-Init-Data': initData };
    }
  }

  // Web: JWT из localStorage
  const jwt = localStorage.getItem('stems_jwt');
  if (jwt) {
    return { 'Authorization': `Bearer ${jwt}` };
  }

  return {};
}

/**
 * Возвращает данные пользователя из WebApp SDK
 */
export function getWebAppUser() {
  const webapp = getWebApp();
  if (!webapp) return null;

  // initDataUnsafe.user
  if (webapp.initDataUnsafe?.user) {
    return webapp.initDataUnsafe.user;
  }

  // Пробуем распарсить initData вручную
  if (webapp.initData) {
    try {
      const params = new URLSearchParams(webapp.initData);
      const userParam = params.get('user');
      if (userParam) return JSON.parse(userParam);
    } catch (e) {
      console.error('Failed to parse initData:', e);
    }
  }

  return null;
}

// Состояние запроса права писать: где уже спрашивали и чем кончилось.
// _writeAccessGranted=true — согласие получено, больше не беспокоим.
const _writeAccessAskedAt = new Set();
let _writeAccessGranted = false;

/**
 * Право писать пользователю — для открывших мини-апп без Старта бота (прямая
 * ссылка t.me/<bot>/<app>). У стартовавших в initData уже allows_write_to_pm=
 * true, им ничего не показывается (решение 2026-08-17). Без разрешения все
 * уведомления о заказе молча падают в «chat not found».
 *
 * Двухступенчатая схема (решение 2026-08-17): при открытии мини-аппа и
 * повторно на «Оформить» в корзине — только если в первый раз согласия не
 * было. Согласился где угодно — больше не всплывает; каждая ступень
 * спрашивает максимум раз за сессию.
 *
 * UI живёт в React (NotifyPermissionPopup — брендовый попап «зачем», стили из
 * бренд-пака; решение 2026-08-17 вместо голого showConfirm): вызывающий код
 * проверяет canAskWriteAccess(stage) → markWriteAccessAsked(stage) → рисует
 * попап → по «Разрешить» зовёт requestWriteAccessNative() (системный диалог
 * Telegram, его текст менять нельзя).
 *
 * Только Telegram: в Max аналога нет и не нужно — проверено экспериментом
 * 17.08, Max-бот может писать любому открывшему мини-апп и без «Начать».
 */
export function canAskWriteAccess(stage) {
  if (getPlatform() !== 'telegram') return false;
  if (_writeAccessGranted || _writeAccessAskedAt.has(stage)) return false;

  const webapp = window.Telegram?.WebApp;
  const user = getWebAppUser();
  // Признак есть только у TG; отсутствие поля = бот писать не может
  if (!webapp || !user || user.allows_write_to_pm === true) return false;
  // requestWriteAccess появился в Bot API 6.9 — на старых клиентах метода нет
  return typeof webapp.requestWriteAccess === 'function';
}

export function markWriteAccessAsked(stage) {
  _writeAccessAskedAt.add(stage);
}

export function requestWriteAccessNative() {
  try {
    window.Telegram?.WebApp?.requestWriteAccess((granted) => {
      if (granted) _writeAccessGranted = true;
    });
  } catch (e) {
    // Отказ или закрытие диалога — не наша забота
  }
}

/**
 * Открыть внешнюю ссылку
 */
export function openLink(url) {
  const webapp = getWebApp();
  const platform = getPlatform();
  const device = getDevicePlatform();

  // Max десктоп: пробуем SDK openLink, fallback на window.open
  if (platform === 'max' && device === 'desktop') {
    if (webapp?.openLink) {
      webapp.openLink(url);
    } else {
      window.open(url, '_blank');
    }
    return;
  }

  if (webapp?.openLink) {
    webapp.openLink(url, { try_instant_view: false });
  } else {
    window.open(url, '_blank');
  }
}

/**
 * Нативный Telegram-инвойс (Bot Payments). Возвращает true, если инвойс
 * открыт; false — клиент не умеет (не Telegram / старая версия), зовущий
 * код падает обратно на redirect-флоу. callback получает статус:
 * 'paid' | 'cancelled' | 'failed' | 'pending'.
 */
export function canOpenInvoice() {
  const webapp = getWebApp();
  // Telegram Web (weba/webk) метод имеет, но платежи сторонних провайдеров
  // не поддерживает («does not support payments with this provider yet») —
  // там оставляем redirect-флоу на страницу ЮКассы.
  if (webapp?.platform === 'weba' || webapp?.platform === 'webk') return false;
  // Помимо getPlatform() смотрим напрямую на initData Telegram: страховка от
  // раннего вердикта «web» при открытии без ?platform=... (direct-link)
  const isTelegram = getPlatform() === 'telegram' || !!window.Telegram?.WebApp?.initData;
  return isTelegram && typeof webapp?.openInvoice === 'function';
}

export function openInvoice(url, callback) {
  const webapp = getWebApp();
  if (!canOpenInvoice()) return false;
  try {
    webapp.openInvoice(url, callback);
    return true;
  } catch (e) {
    console.error('openInvoice failed:', e);
    return false;
  }
}

/**
 * Открыть ссылку внутри платформы (Telegram chat / Max chat)
 */
export function openPlatformLink(url) {
  const platform = getPlatform();
  const webapp = getWebApp();

  if (platform === 'telegram' && webapp?.openTelegramLink) {
    webapp.openTelegramLink(url);
  } else if (platform === 'max' && webapp?.openMaxLink) {
    webapp.openMaxLink(url);
  } else {
    window.open(url, '_blank');
  }
}

/**
 * Показать диалог подтверждения
 */
export function showConfirm(message, callback) {
  const webapp = getWebApp();
  if (webapp?.showConfirm) {
    webapp.showConfirm(message, callback);
  } else {
    const result = window.confirm(message);
    callback(result);
  }
}

/**
 * HapticFeedback — вибрация
 */
export function getHapticFeedback() {
  const webapp = getWebApp();
  return webapp?.HapticFeedback || null;
}

/**
 * Инициализация платформы (вызывать при загрузке приложения)
 */
export function initPlatform() {
  const webapp = getWebApp();
  if (!webapp) return;

  webapp.ready();
  if (webapp.expand) webapp.expand();
  if (webapp.setHeaderColor) webapp.setHeaderColor('#FFFFFF');
  if (webapp.setBackgroundColor) webapp.setBackgroundColor('#FFFFFF');
}

/**
 * Получить start_param (deep link параметр)
 */
export function getStartParam() {
  const webapp = getWebApp();
  const fromSdk = webapp?.initDataUnsafe?.start_param || null;
  if (fromSdk) return fromSdk;
  // Fallback для web: после редиректа с оплаты приходит ?p=payment_123 / payment_sub_456
  if (typeof window !== 'undefined') {
    return new URLSearchParams(window.location.search).get('p') || null;
  }
  return null;
}

/**
 * Закрыть мини-приложение
 */
export function closeApp() {
  const webapp = getWebApp();
  if (webapp?.close) {
    webapp.close();
  }
}
